import { createPersistedStore } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';
import type { UploadedFile } from '../components/service-contract/SCSignScreen';
import { validateSCUploadForm } from '../schemas/sc-upload.schema';
import {
  DEFAULT_ATTEMPT_SERVICE_TYPE,
  type AttemptServiceType,
} from '../types/attempt-service-type';

/**
 * Service Contract Upload store using Zustand with store-utils
 * Manages upload state for Service Contract documents — shared by Service
 * Request and Bundle Request (TDF-1342 Phase 3). `srAttemptId` holds
 * whichever attempt id applies; `serviceType` decides which field name
 * `getFormData()` sends it under.
 *
 * NOTE: This store only manages UI state for files.
 * API calls should be made via useSCApi hook in components.
 */

interface SCUploadState extends BaseStoreState {
  // File state
  uploadedFiles: UploadedFile[];
  rejectionReason: string | null;
  // Actions (included in state for proper typing)
  setUploadedFiles: (_files: UploadedFile[]) => void;
  addFile: (_file: UploadedFile) => void;
  updateFile: (_fileId: string, _updates: Partial<UploadedFile>) => void;
  removeFile: (_fileId: string) => void;
  clearFiles: () => void;
  reset: () => void;
  setUploadError: (_error: string | null) => void;
  setRejectionReason: (_reason: string | null) => void;
  validateFiles: () => { success: boolean; error?: string };
  getFormData: () => FormData | null;
  srAttemptId: string;
  setSrAttemptId: (_srAttemptId: string) => void;
  serviceType: AttemptServiceType;
  setServiceType: (_serviceType: AttemptServiceType) => void;
}

const initialFormData: Omit<SCUploadState, keyof BaseStoreState> = {
  uploadedFiles: [],
  rejectionReason: null,
  // Placeholder functions - will be overwritten by customActions
  setUploadedFiles: () => {},
  addFile: () => {},
  updateFile: () => {},
  removeFile: () => {},
  clearFiles: () => {},
  reset: () => {},
  setUploadError: () => {},
  setRejectionReason: () => {},
  validateFiles: () => ({ success: false }),
  getFormData: () => null,
  srAttemptId: '',
  setSrAttemptId: () => {},
  serviceType: DEFAULT_ATTEMPT_SERVICE_TYPE,
  setServiceType: () => {},
};

export const useSCUploadStore = createPersistedStore<SCUploadState>(
  'sc-upload',
  initialFormData,
  (set, get) => ({
    // Actions
    setSrAttemptId: (srAttemptId: string) => {
      set({ srAttemptId } as Partial<SCUploadState>);
    },

    setServiceType: (serviceType: AttemptServiceType) => {
      set({ serviceType } as Partial<SCUploadState>);
    },

    setUploadedFiles: (files: UploadedFile[]) => {
      set({ uploadedFiles: files } as Partial<SCUploadState>);
    },

    addFile: (file: UploadedFile) => {
      const { uploadedFiles } = get();
      set({
        uploadedFiles: [...uploadedFiles, file],
      } as Partial<SCUploadState>);
    },

    updateFile: (fileId: string, updates: Partial<UploadedFile>) => {
      const { uploadedFiles } = get();
      const updatedFiles = uploadedFiles.map((file: UploadedFile) =>
        file.id === fileId ? { ...file, ...updates } : file
      );
      set({ uploadedFiles: updatedFiles } as Partial<SCUploadState>);
    },

    removeFile: (fileId: string) => {
      const { uploadedFiles } = get();
      const filteredFiles = uploadedFiles.filter(
        (file: UploadedFile) => file.id !== fileId
      );
      set({ uploadedFiles: filteredFiles } as Partial<SCUploadState>);
    },

    clearFiles: () => {
      set({ uploadedFiles: [] } as Partial<SCUploadState>);
    },

    reset: () => {
      set({
        uploadedFiles: [],
        loading: false,
        error: null,
      } as Partial<SCUploadState>);
    },

    setUploadError: (error: string | null) => {
      set({ error } as Partial<SCUploadState>);
    },

    setRejectionReason: (rejectionReason: string | null) => {
      set({ rejectionReason } as Partial<SCUploadState>);
    },

    /**
     * Validate files before upload
     * Returns validation result with optional error message
     */
    validateFiles: () => {
      const { uploadedFiles } = get();

      if (uploadedFiles.length === 0) {
        return { success: false, error: 'Please select a file to upload' };
      }

      const validationResult = validateSCUploadForm({ uploadedFiles });

      if (!validationResult.success && validationResult.errors) {
        const firstError = Object.values(validationResult.errors)[0];
        return { success: false, error: firstError || 'Validation failed' };
      }

      return { success: true };
    },

    /**
     * Get FormData for the first uploaded file
     * Returns null if no file is available
     */
    getFormData: () => {
      const { uploadedFiles, srAttemptId, serviceType } = get();
      const uploadFile = uploadedFiles[0];

      if (!uploadFile?.file) {
        return null;
      }

      const formData = new FormData();
      if (srAttemptId) {
        const idField =
          serviceType === 'bundle' ? 'bundleAttemptId' : 'srAttemptId';
        formData.append(idField, srAttemptId);
      }
      formData.append('file', uploadFile.file);
      return formData;
    },
  }),
  {
    partialize: (state) => ({
      // Don't persist file objects or buffers
      uploadedFiles: state.uploadedFiles.map((file: UploadedFile) => ({
        id: file.id,
        fileName: file.fileName,
        fileSize: file.fileSize,
        bytes: file.bytes || 0,
        progress: file.progress,
        status: file.status,
        // Exclude the actual File object from persistence
        file: undefined as unknown as File,
      })),
      srAttemptId: state.srAttemptId,
      serviceType: state.serviceType,
    }),
    clearOnRefresh: ['uploadedFiles'],
  }
);

// Selectors for convenience
export const useSCUploadFiles = () =>
  useSCUploadStore((state) => state.uploadedFiles);
export const useSCUploadIsUploading = () =>
  useSCUploadStore((state) => state.loading);
export const useSCUploadError = () => useSCUploadStore((state) => state.error);
